function ToolPanel({ 
    toolPanelVisible,
    toggleToolPanel,
    currentTool,
    handleToolChange,
    toolSettings,
    handleToolSettingChange,
    canvasBackground,
    handleBackgroundChange,
    isSubscriber,
    rulerVisible,
    onToggleSavedStrokes,
    onPlaceShape // New prop for placing predefined shapes
}) {

    if (!toolPanelVisible) { return null }

    // Custom handler to set highlighter defaults
    const handleToolButtonClick = (tool) => {
        handleToolChange(tool);
        if (tool === 'highlighter') {
            // Set default thickness and opacity for highlighter
            if (toolSettings.thickness !== 20) handleToolSettingChange('thickness', 20);
            if (toolSettings.opacity !== 0.5) handleToolSettingChange('opacity', 0.5);
        }
    };

    return (
        <div className={`floating-tool-panel modern ${!toolPanelVisible ? 'hidden' : ''}`}>
            <div className="panel-header-modern">
                <div className="panel-title-row">
                    <span className="panel-icon">
                        <i className="fas fa-palette"></i>
                    </span>
                    <h4>Drawing Tools</h4>
                </div>
                <button 
                    className="close-panel-button-modern"
                    onClick={toggleToolPanel}
                    title="Close"
                    aria-label="Close tools panel"
                >
                    <i className="fas fa-times"></i>
                </button>
            </div>
            
            {/* Main Tools Section */}
            <div className="tool-section-modern">
                <div className="tool-section-label">Main Tools</div>
                <div className="tool-grid-modern">
                    <button 
                        className={`tool-btn-modern ${currentTool === 'pen' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('pen')}
                        title="Pen"
                        aria-label="Pen tool"
                    >
                        <i className="fas fa-pen"></i>
                        <span className="tool-label">Pen</span>
                    </button>
                    <button 
                        className={`tool-btn-modern ${currentTool === 'highlighter' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('highlighter')}
                        title="Highlighter"
                        aria-label="Highlighter tool"
                    >
                        <i className="fas fa-highlighter"></i>
                        <span className="tool-label">Highlight</span>
                    </button>
                    <button 
                        className={`tool-btn-modern ${currentTool === 'eraser' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('eraser')}
                        title="Point Eraser"
                        aria-label="Point eraser tool"
                    >
                        <i className="fas fa-eraser"></i>
                        <span className="tool-label">Eraser</span>
                    </button>
                    <button 
                        className={`tool-btn-modern ${rulerVisible ? 'active' : ''}`}
                        onClick={() => handleToolChange('ruler')}
                        title="Ruler"
                        aria-label="Ruler tool for straight lines"
                    >
                        <i className="fas fa-ruler"></i>
                        <span className="tool-label">Ruler</span>
                    </button>
                    <button 
                        className={`tool-btn-modern ${currentTool === 'select' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('select')}
                        title="Selection"
                        aria-label="Selection tool"
                    >
                        <i className="fas fa-mouse-pointer"></i>
                        <span className="tool-label">Select</span>
                    </button>
                    <button 
                        className={`tool-btn-modern ${currentTool === 'text' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('text')}
                        title="Text"
                        aria-label="Text tool"
                    >
                        <i className="fas fa-font"></i>
                        <span className="tool-label">Text</span>
                    </button>
                </div>
            </div>

            {/* Advanced Erasers Section */}
            <div className="tool-section-modern tool-section-compact">
                <div className="tool-section-label">Advanced Erasers</div>
                <div className="tool-grid-horizontal">
                    <button 
                        className={`tool-btn-modern-compact ${currentTool === 'square-eraser' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('square-eraser')}
                        title="Square Area Eraser"
                        aria-label="Square area eraser"
                    >
                        <i className="far fa-square"></i>
                    </button>
                    <button 
                        className={`tool-btn-modern-compact ${currentTool === 'circle-eraser' ? 'active' : ''}`}
                        onClick={() => handleToolButtonClick('circle-eraser')}
                        title="Circle Area Eraser"
                        aria-label="Circle area eraser"
                    >
                        <i className="far fa-circle"></i>
                    </button>
                </div>
            </div>

            {/* Saved Strokes Section */}
            <div className="tool-section-modern tool-section-compact">
                <div className="tool-section-label">Library</div>
                <div className="tool-grid-horizontal">
                    <button 
                        className="tool-btn-modern-compact library-btn"
                        onClick={onToggleSavedStrokes}
                        title="Saved Strokes"
                        aria-label="Saved strokes library"
                    >
                        <i className="fas fa-bookmark"></i>
                        <span className="tool-label-compact">Saved</span>
                    </button>
                </div>
            </div>

            {/* Color Palette */}
            <div className="tool-section-modern">
                <div className="tool-section-label">Color Palette</div>
                <div className="color-palette-modern">
                    <button 
                        className={`color-swatch ${toolSettings.color === '#000000' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#000000' }}
                        onClick={() => handleToolSettingChange('color', '#000000')}
                        title="Black"
                        aria-label="Black color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#ffffff' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#ffffff' }}
                        onClick={() => handleToolSettingChange('color', '#ffffff')}
                        title="White"
                        aria-label="White color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#ef4444' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#ef4444' }}
                        onClick={() => handleToolSettingChange('color', '#ef4444')}
                        title="Red"
                        aria-label="Red color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#3b82f6' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#3b82f6' }}
                        onClick={() => handleToolSettingChange('color', '#3b82f6')}
                        title="Blue"
                        aria-label="Blue color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#eab308' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#eab308' }}
                        onClick={() => handleToolSettingChange('color', '#eab308')}
                        title="Yellow"
                        aria-label="Yellow color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#22c55e' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#22c55e' }}
                        onClick={() => handleToolSettingChange('color', '#22c55e')}
                        title="Green"
                        aria-label="Green color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#a855f7' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#a855f7' }}
                        onClick={() => handleToolSettingChange('color', '#a855f7')}
                        title="Purple"
                        aria-label="Purple color"
                    >
                        <span className="color-fill"></span>
                    </button>
                    <button 
                        className={`color-swatch ${toolSettings.color === '#f97316' ? 'active' : ''}`}
                        style={{ '--swatch-color': '#f97316' }}
                        onClick={() => handleToolSettingChange('color', '#f97316')}
                        title="Orange"
                        aria-label="Orange color"
                    >
                        <span className="color-fill"></span>
                    </button>
                </div>
                <div className="custom-color-picker-wrapper">
                    <label htmlFor="custom-color-input" className="custom-color-label">
                        <i className="fas fa-eyedropper"></i>
                        <span>Custom Color</span>
                    </label>
                    <input
                        id="custom-color-input"
                        type="color"
                        value={toolSettings.color}
                        onChange={(e) => handleToolSettingChange('color', e.target.value)}
                        className="custom-color-input"
                        title="Pick custom color"
                    />
                </div>
            </div>

            {/* Pen Settings */}
            <div className="tool-section-modern">
                <div className="tool-section-label">
                    <span>Thickness</span>
                    <span className="setting-value">{toolSettings.thickness}px</span>
                </div>
                <div className="slider-container-modern">
                    <input
                        type="range"
                        min="1"
                        max="20"
                        value={toolSettings.thickness}
                        onChange={(e) => handleToolSettingChange('thickness', Number(e.target.value))}
                        className="modern-slider"
                        aria-label="Pen thickness"
                    />
                </div>
            </div>

            <div className="tool-section-modern">
                <div className="tool-section-label">
                    <span>Opacity</span>
                    <span className="setting-value">{Math.round(toolSettings.opacity * 100)}%</span>
                </div>
                <div className="slider-container-modern">
                    <input
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.1"
                        value={toolSettings.opacity}
                        onChange={(e) => handleToolSettingChange('opacity', Number(e.target.value))}
                        className="modern-slider"
                        aria-label="Pen opacity"
                    />
                </div>
            </div>

            {/* Background Patterns */}
            <div className="tool-section-modern">
                <div className="tool-section-label">Background</div>
                <select
                    value={canvasBackground}
                    onChange={(e) => handleBackgroundChange(e.target.value)}
                    className="modern-select"
                    aria-label="Canvas background"
                >
                    <option value="white">Plain White</option>
                    <option value="lined">Lined Paper</option>
                    <option value="lined-black">Lined Paper (Black)</option>
                    <option value="lined-wide">Wide Lined Paper</option>
                    <option value="lined-extra-wide">Extra Wide Lined Paper</option>
                    <option value="grid">Grid Paper</option>
                    <option value="grid-wide">Wide Grid Paper</option>
                    {isSubscriber ? (
                        <>
                            <option value="dot">Dot Grid</option>
                            <option value="dot-wide">Wide Dot Grid</option>
                            <option value="dots-minimal">Minimalist Dots</option>
                            <option value="leaves">Leaves</option>
                            <option value="isometric">Isometric Grid</option>
                            <option value="hex">Hexagonal Grid</option>
                            <option value="cornell">Cornell Notes</option>
                            <option value="music">Music Staff</option>
                            <option value="blueprint">Blueprint</option>
                            <option value="watercolor">Watercolor Wash</option>
                            <option value="gradient">Soft Gradient</option>
                            <option value="galaxy">Galaxy/Stars</option>
                            <option value="kanban">Kanban Columns</option>
                            <option value="calendar">Calendar/Planner</option>
                            <option value="mindmap">Mind Map Nodes</option>
                        </>
                    ) : (
                        <>
                            <option value="dot" disabled>Dot Grid (Subscribers Only)</option>
                            <option value="dot-wide" disabled>Wide Dot Grid (Subscribers Only)</option>
                            <option value="dots-minimal" disabled>Minimalist Dots (Subscribers Only)</option>
                            <option value="leaves" disabled>Leaves (Subscribers Only)</option>
                            <option value="isometric" disabled>Isometric Grid (Subscribers Only)</option>
                            <option value="hex" disabled>Hexagonal Grid (Subscribers Only)</option>
                            <option value="cornell" disabled>Cornell Notes (Subscribers Only)</option>
                            <option value="music" disabled>Music Staff (Subscribers Only)</option>
                            <option value="blueprint" disabled>Blueprint (Subscribers Only)</option>
                            <option value="watercolor" disabled>Watercolor Wash (Subscribers Only)</option>
                            <option value="gradient" disabled>Soft Gradient (Subscribers Only)</option>
                            <option value="galaxy" disabled>Galaxy/Stars (Subscribers Only)</option>
                            <option value="kanban" disabled>Kanban Columns (Subscribers Only)</option>
                            <option value="calendar" disabled>Calendar/Planner (Subscribers Only)</option>
                            <option value="mindmap" disabled>Mind Map Nodes (Subscribers Only)</option>
                        </>
                    )}
                </select>
            </div>
        </div>
    );
}

export default ToolPanel;